import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { hexahqCacheApi } from '../api/services';
import { extractApiError } from '../utils/apiErrors';

function timeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  return `${Math.floor(seconds / 86400)} days ago`;
}

export const CacheManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const { data: skuStatus, isLoading: skuLoading } = useQuery({
    queryKey: ['hexahq_sku_cache_status'],
    queryFn: hexahqCacheApi.skuStatus,
  });

  const { data: accountStatus, isLoading: accountLoading } = useQuery({
    queryKey: ['hexahq_account_cache_status'],
    queryFn: hexahqCacheApi.accountStatus,
  });

  const skuSync = useMutation({
    mutationFn: hexahqCacheApi.skuSync,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hexahq_sku_cache_status'] });
      setSnackbar({
        message: `SKU sync complete: ${data.added} added, ${data.removed} removed, ${data.updated} updated (${data.total} total)`,
        severity: 'success',
      });
    },
    onError: (error: unknown) => {
      setSnackbar({
        message: extractApiError(error, 'SKU sync failed'),
        severity: 'error',
      });
    },
  });

  const accountSync = useMutation({
    mutationFn: hexahqCacheApi.accountSync,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hexahq_account_cache_status'] });
      setSnackbar({
        message: `Account sync complete: ${data.added} added, ${data.removed} removed, ${data.updated} updated (${data.total} total)`,
        severity: 'success',
      });
    },
    onError: (error: unknown) => {
      setSnackbar({
        message: extractApiError(error, 'Account sync failed'),
        severity: 'error',
      });
    },
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        HexaHQ Sync
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage cached data synced from HexaHQ. These caches are used for SKU and account lookups.
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 300, flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              SKU Cache
            </Typography>
            {skuLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <Typography variant="h3" sx={{ my: 1 }}>
                  {skuStatus?.count ?? '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last synced: {timeAgo(skuStatus?.last_synced_at ?? null)}
                </Typography>
              </>
            )}
          </CardContent>
          <CardActions>
            <Button
              startIcon={skuSync.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={() => skuSync.mutate()}
              disabled={skuSync.isPending}
            >
              {skuSync.isPending ? 'Syncing...' : 'Sync Now'}
            </Button>
          </CardActions>
        </Card>

        <Card sx={{ minWidth: 300, flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Account Cache
            </Typography>
            {accountLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <Typography variant="h3" sx={{ my: 1 }}>
                  {accountStatus?.count ?? '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last synced: {timeAgo(accountStatus?.last_synced_at ?? null)}
                </Typography>
              </>
            )}
          </CardContent>
          <CardActions>
            <Button
              startIcon={accountSync.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={() => accountSync.mutate()}
              disabled={accountSync.isPending}
            >
              {accountSync.isPending ? 'Syncing...' : 'Sync Now'}
            </Button>
          </CardActions>
        </Card>
      </Box>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity} variant="filled">
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
